import React, { useEffect, useState } from 'react';
import { Col, Form, Input, Row, Select } from 'antd';
import Payment from '../../../classes/payment';
import { Utils } from '../../../tools/utils';
import { ValidationPatterns } from '../../../config/config';
import { useTranslation } from 'react-i18next';
import { useStore } from 'store';
import SicuriProtettiServices from 'js/data/sicuri-protetti-services';

const { postalCodePattern, ibanPattern, swiftPattern } = ValidationPatterns;
const utils = new Utils();
const { Option } = Select;
const twoColFormItemLayout = {
    wrapperCol: { span: 14 },
    labelCol: { span: 6 },
};

interface IProps {
    data: Payment;
    formRef: any;
}

const SepaForm = (props: IProps) => {
    const { t } = useTranslation();
    const setLoading = useStore((state) => state.setLoading);
    const [states, setStates] = useState<simpleStruct[]>([]);
    const [provinces, setProvinces] = useState<simpleStruct[]>([]);
    const [cities, setCities] = useState<simpleStruct[]>([]);

    const onStateChange = async (values: any) => {
        props.formRef.current!.setFieldsValue({ cityId: undefined, province: undefined, postalCode: undefined });
        let provinces;
        const cities = await SicuriProtettiServices.getCities(values);
        if (values === 'IT') provinces = await SicuriProtettiServices.getProvinces();
        else provinces = [];
        setProvinces(provinces);
        setCities(cities);
    };

    const onProvinceSelected = async (value: any) => {
        props.formRef.current!.setFieldsValue({ cityId: undefined, postalCode: undefined });
        const cities = await SicuriProtettiServices.getCitiesByProvince(props.formRef.current!.getFieldValue('nationId'), value);
        setCities(cities);
    };

    const onCitySelected = async (value: any) => {
        props.formRef.current!.setFieldsValue({
            postalCode: document.getElementById('city-' + value)!.getAttribute('data-postalcode'),
            province: document.getElementById('city-' + value)!.getAttribute('data-province'),
        });
    };

    useEffect(() => {
        const getData = async () => {
            setLoading(true);
            const states = await SicuriProtettiServices.getStates();
            const provinces = await SicuriProtettiServices.getProvinces();
            const cities = await SicuriProtettiServices.getCities(props.data.nationId);
            setStates(states);
            setProvinces(provinces);
            setCities(cities);
            setLoading(false);
        };

        getData();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Row>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="iban"
                        label={t('customerClass.payments.iban')}
                        initialValue={props.data.iban}
                        rules={[
                            {
                                required: true,
                                message: t('errorMessages.ibanReq'),
                            },
                            {
                                pattern: ibanPattern,
                                message: t('errorMessages.ibanValid'),
                            },
                        ]}
                    >
                        <Input placeholder={t('customerClass.payments.iban')} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="swift"
                        label={t('customerClass.payments.swift')}
                        initialValue={props.data.swift}
                        rules={[
                            /*{
                                required: true,
                                message: t("errorMessages.swiftReq")
                            },*/
                            {
                                pattern: swiftPattern,
                                message: t('errorMessages.swiftValid'),
                            },
                        ]}
                    >
                        <Input placeholder={t('customerClass.payments.swift')} />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="accountHolder"
                        label={t('customerClass.payments.accountHolder')}
                        initialValue={props.data.accountHolder}
                        rules={[
                            {
                                required: true,
                                message: t('errorMessages.accountHolderReq'),
                            },
                        ]}
                    >
                        <Input placeholder={t('customerClass.payments.accountHolder')} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="accountSignatory"
                        label={t('customerClass.payments.accountSignatory')}
                        initialValue={props.data.accountSignatory}
                        rules={[
                            {
                                required: true,
                                message: t('errorMessages.accountSignatoryReq'),
                            },
                        ]}
                    >
                        <Input placeholder={t('customerClass.payments.accountSignatory')} />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="fiscalId"
                        label={t('customerClass.fiscalIdSN')}
                        initialValue={props.data.fiscalId}
                        rules={[
                            {
                                required: true,
                                validator: utils.ValidateFiscalId,
                                message: t('errorMessages.fiscalIdReq'),
                            },
                        ]}
                    >
                        <Input placeholder={t('customerClass.fiscalIdSN')} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="nationId"
                        label={t('commonClass.nation')}
                        initialValue={props.data.nationId}
                        rules={[
                            {
                                required: true,
                                message: t('errorMessages.nationReq'),
                            },
                        ]}
                    >
                        <Select
                            placeholder={t('commonClass.nation')}
                            showSearch
                            optionFilterProp="children"
                            onChange={onStateChange}
                            filterOption={(input, option) => option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        >
                            {states.map((state) => (
                                <Option key={state.value} value={state.value} id={'nation-' + state.value} data-nation={state.label}>
                                    {state.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="province"
                        label={t('commonClass.province')}
                        initialValue={props.data.province}
                    >
                        <Select
                            placeholder={t('commonClass.province')}
                            showSearch
                            optionFilterProp="children"
                            onSelect={onProvinceSelected}
                            filterOption={(input, option) => option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        >
                            {provinces.map((province) => (
                                <Option
                                    key={province.value}
                                    value={province.value}
                                    id={'province-' + province.value}
                                    data-state={province.label}
                                >
                                    {province.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        label={t('customerClass.cityOfResidence')}
                        initialValue={props.data.cityId}
                        name="cityId"
                        rules={[
                            {
                                required: true,
                                message: t('errorMessages.cityOfResidenceReq'),
                            },
                        ]}
                    >
                        <Select
                            placeholder={t('customerClass.cityOfResidence')}
                            showSearch
                            onSelect={onCitySelected}
                            optionFilterProp="children"
                            filterOption={(input, option) => option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        >
                            {cities.map((city) => (
                                <Option
                                    key={city.value}
                                    value={city.value}
                                    id={'city-' + city.value}
                                    data-city={city.label}
                                    data-province={city.additionalData[0]}
                                    data-postalcode={city.additionalData[1]}
                                >
                                    {city.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="postalCode"
                        label={t('customerClass.postalCode')}
                        initialValue={props.data.postalCode}
                        rules={[
                            {
                                required: true,
                                message: t('errorMessages.postalCodeReq'),
                            },
                            {
                                pattern: postalCodePattern,
                                message: t('errorMessages.postalCodeValid'),
                            },
                        ]}
                    >
                        <Input placeholder={t('customerClass.postalCodeSN')} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <Form.Item
                        {...twoColFormItemLayout}
                        name="address"
                        label={t('customerClass.address')}
                        initialValue={props.data.address}
                        rules={[
                            {
                                required: true,
                                message: t('errorMessages.residentialAddressReq'),
                            },
                        ]}
                    >
                        <Input placeholder={t('customerClass.address')} />
                    </Form.Item>
                </Col>
            </Row>
        </>
    );
};

export default SepaForm;
